import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Loader2, UserCheck, Inbox } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { fmtDate, fmtTime } from './shared';

const API = `${window.__BACKEND_URL__}/api`;

const ymd = (d) => d.toISOString().slice(0, 10);

/** Supervisor "certify for" — closed shifts the worker hasn't certified (kiosk crews). */
export default function UncertifiedList({ token, onChanged }) {
  const { toast } = useToast();
  const [entries, setEntries] = useState(null);
  const [range, setRange] = useState({ start: ymd(new Date(Date.now() - 13 * 86400000)), end: ymd(new Date()) });
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/time/uncertified`, {
        headers: { Authorization: `Bearer ${token}` }, params: { start_date: range.start, end_date: range.end } });
      setEntries(data.entries || []);
    } catch (e) { setEntries([]); }
  }, [token, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const certify = async () => {
    if (!target || reason.trim().length < 10) return;
    setBusy(true);
    try {
      await axios.put(`${API}/time/${target.id}/supervisor-certify-for`, { reason: reason.trim() }, auth);
      toast({ title: 'Shift certified & approved', description: `${target.user_name} · ${fmtDate(target.clock_in)} — worker notified.` });
      setTarget(null); setReason('');
      load(); onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: 'Could not certify', description: typeof d === 'string' ? d : d?.message || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const inputStyle = { background: '#1A2433', color: '#fff', border: '1px solid #2D3748' };

  return (
    <div className="space-y-3" data-testid="uncertified-list">
      <div className="ht-card p-3 flex flex-wrap items-center gap-2">
        <p className="text-sm text-white font-semibold flex items-center gap-2"><UserCheck className="w-4 h-4" style={{ color: '#00C896' }} /> Shifts not certified by the worker</p>
        <div className="ml-auto flex items-center gap-2">
          <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="uncertified-start" />
          <span style={{ color: '#64748B' }}>→</span>
          <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="uncertified-end" />
        </div>
      </div>
      <p className="text-xs" style={{ color: '#A0AEC0' }}>
        For crews without phones (kiosk punches) you can certify a shift on the worker's behalf — it is approved in the same step, the reason is audit-logged and the worker is notified.
      </p>
      {entries === null ? (
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#1E88E5' }} />
      ) : entries.length === 0 ? (
        <div className="ht-card p-6 text-center" data-testid="uncertified-empty">
          <Inbox className="w-6 h-6 mx-auto mb-2" style={{ color: '#64748B' }} />
          <p className="text-sm" style={{ color: '#A0AEC0' }}>Everyone has certified their shifts in this range.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {entries.map((e) => (
            <div key={e.id} className="ht-card px-3 py-2 flex items-center justify-between gap-3" data-testid={`uncertified-entry-${e.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-white font-semibold truncate">{e.user_name} · {fmtDate(e.clock_in)}</p>
                <p className="text-[11px]" style={{ color: '#A0AEC0' }}>
                  {fmtTime(e.clock_in)} → {fmtTime(e.clock_out)} · {e.hours} h
                  {e.clock_out_source === 'kiosk' && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(45,212,191,0.15)', color: '#2DD4BF' }}>KIOSK</span>}
                  {e.approval_status === 'returned' && <span style={{ color: '#FB923C' }}> · returned{e.returned_reason ? `: "${e.returned_reason}"` : ''}</span>}
                </p>
              </div>
              <Button size="sm" onClick={() => setTarget(e)} className="bg-[#00C896] hover:bg-emerald-600 text-white whitespace-nowrap" data-testid={`certify-for-btn-${e.id}`}>
                Certify for worker
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <DialogContent className="max-w-md" data-testid="certify-for-dialog">
          <DialogHeader>
            <DialogTitle>Certify on the worker's behalf</DialogTitle>
            <DialogDescription>
              {target && <>{target.user_name} · {fmtDate(target.clock_in)} · {target.hours} h. You confirm these hours are correct; the shift becomes <strong>supervisor-approved</strong> and the worker is notified.</>}
            </DialogDescription>
          </DialogHeader>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Reason (min 10 characters) — e.g. Kiosk crew without phones; hours verified on site."
            className="w-full rounded-lg px-3 py-2 text-sm" style={inputStyle} data-testid="certify-for-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={busy} data-testid="certify-for-cancel">Cancel</Button>
            <Button onClick={certify} disabled={busy || reason.trim().length < 10} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="certify-for-confirm">
              {busy ? 'Certifying…' : 'Certify & approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
